if (!customElements.get('custom-modal')) {
  customElements.define('custom-modal', class Modal extends HTMLElement {
    constructor() {
      super();
      this.activeIds        = [];
      this.onKeydown        = e => e.key === 'Escape' && this.close();
      this.observer         = new MutationObserver(() => this.handleContentUpdate());
      this._focusTrapCleanups = new Map();
      this._openers           = new Map();
    }

    _retrapFocus(id, focusDelay = 0) {
      const el = this.querySelector(`[data-modal-content="${id}"]:not(template)`);
      if (!el) return;
      this._focusTrapCleanups.get(id)?.();
      this._focusTrapCleanups.set(id, trapFocus(el, { focusDelay }));
    }

    handleContentUpdate() {
      const id = this.activeIds.at(-1);
      if (id) this._retrapFocus(id);
    }

    animateIn() {
      const id = this.activeIds.at(-1);
      const el = this.querySelector(`[data-modal-content="${id}"]:not(template)`);
      if (!el) return;
      el.classList.remove('modal-invisible');
      el.classList.add('modal-visible');
      this._retrapFocus(id, 50);
    }

    animateOut(id) {
      const el = this.querySelector(`[data-modal-content="${id}"]:not(template)`);
      if (!el) return;
      const wasVisible = el.classList.contains('modal-visible');
      el.classList.remove('modal-visible');
      el.classList.add('modal-invisible');

      const cleanup = () => {
        this._focusTrapCleanups.get(id)?.();
        this._focusTrapCleanups.delete(id);
        const template = document.querySelector(`[data-modal-content="${id}"]`);
        if (template && !template.dataset.noSync) {
          const html = el.innerHTML;
          (window.requestIdleCallback ?? window.setTimeout)(() => { template.innerHTML = html; });
        }
        el.remove();
        window.dispatchEvent(new CustomEvent('modal:closed', { detail: id }));
        if (!this.activeIds.length) window.removeEventListener('keydown', this.onKeydown);
        const returnTrigger = this._openers.get(id)
          ?? document.querySelector(`[data-modal-action="open"][data-modal-id="${id}"]:not([data-hover-trigger])`);
        this._openers.delete(id);
        returnTrigger?.setAttribute('aria-expanded', 'false');
        returnTrigger?.focus({ preventScroll: true });
        this.classList.remove('hovered');
      };

      // If the modal was closed before its open transition ever ran (e.g. a quick
      // hover in/out), the class toggle above is a no-op and 'transitionend' will
      // never fire — clean up immediately instead of waiting on an event that won't come.
      if (wasVisible) el.addEventListener('transitionend', cleanup, { once: true });
      else cleanup();
    }

    open(id, closeOnExit, parent) {
      if (!id || this.activeIds.includes(id)) return;

      const template = document.querySelector(`[data-modal-content="${id}"]`);
      if (!template) return;

      this.activeIds.push(id);

      const el = document.createElement('div');
      el.innerHTML = template.cloneNode(true).innerHTML;
      el.classList.add('modal-invisible');
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      [...template.attributes].forEach(a => el.setAttribute(a.name, a.value));
      if (template.dataset.modalLabel) el.setAttribute('aria-label', template.dataset.modalLabel);

      (parent ?? this).appendChild(el);

      // Sync LoyaltyLion points into modal (SDK only scans live DOM at load time, not templates)
      if (el.querySelector('[data-lion-points]')) {
        const source = document.querySelector('[data-account-teaser-panel] [data-lion-points]');
        if (source?.textContent) {
          el.querySelectorAll('[data-lion-points]').forEach(t => { t.textContent = source.textContent; });
        }
      }

      document.querySelector(`[data-modal-action="open"][data-modal-id="${id}"][aria-expanded]`)?.setAttribute('aria-expanded', 'true');

      this.querySelectorAll('.modal-visible:not(:last-of-type) > div').forEach(d => d.classList.add('layered-view'));
      setTimeout(() => this.animateIn(), 1);

      window.addEventListener('keydown', this.onKeydown);

      if (closeOnExit) {
        const target = parent ?? el;
        const event  = parent ? 'mouseleave' : 'mouseenter';
        target.addEventListener(event, () => {
          if (parent) { this.close(id); return; }
          el.addEventListener('mouseleave', () => this.close(id), { once: true });
        }, { once: true });
      }
    }

    close(id = this.activeIds.at(-1)) {
      if (!this.activeIds.includes(id)) return;
      this.activeIds = this.activeIds.filter(x => x !== id);
      this.animateOut(id);
      this.querySelector('.modal-visible > div')?.classList.remove('layered-view');
    }

    closeAll() {
      [...this.activeIds].reverse().forEach(id => this.close(id));
    }

    connectedCallback() {
      this.observer.observe(this, { childList: true, subtree: true });

      const params = new URLSearchParams(location.search);
      if (params.get('reset') && !location.pathname.includes('account')) {
        const link = document.querySelector('a[href="/account"][data-modal-id]');
        if (link) {
          this.open(link.dataset.modalId);
          setTimeout(() => document.querySelector('[data-account-target="forgot-password"]')?.click(), 0);
        }
      }

      window.addEventListener('modal:openById',  e => this.open(e.detail));
      window.addEventListener('modal:closeById', e => this.close(e.detail));

      window.addEventListener('mouseover', e => {
        const trigger = e.target.closest('[data-modal-action="open"][data-hover-trigger]');
        if (!trigger) return;
        const { modalId } = trigger.dataset;
        if (this.activeIds.includes(modalId)) return;
        this.classList.add('hovered');
        this.activeIds = [];
        document.querySelectorAll('[data-hover-trigger] [data-modal-content]').forEach(el => el.remove());
        this.open(modalId, true, trigger);
      });

      // Guard against swiper drags ending outside the drawer on the backdrop closing it.
      let pointerDownOnSwiper = false;
      let draggingSwiper = false;
      window.addEventListener('pointerdown', e => {
        draggingSwiper = false;
        pointerDownOnSwiper = !!e.target.closest('swiper-container, swiper-slide, swiper-wrapper');
      }, { passive: true });
      window.addEventListener('pointermove', e => {
        if (pointerDownOnSwiper) draggingSwiper = true;
      }, { passive: true });

      window.addEventListener('click', e => {
        if (e.target === this) {
          if (draggingSwiper) { draggingSwiper = false; return; }
          this.close();
          return;
        }

        const registerLink = e.target.closest('a[href]');
        if (registerLink && registerLink.pathname === '/account/register' && registerLink.closest('[data-register-link-intercept]')) {
          e.preventDefault();
          const faqDrawer = registerLink.closest('[data-modal-content^="rewards-faq"]');
          if (faqDrawer) this.close(faqDrawer.dataset.modalContent);
          const accountId = document.querySelector('[data-modal-content^="account-drawer-"]')?.dataset.modalContent;
          if (accountId) {
            this.open(accountId);
            setTimeout(() => document.querySelector('[data-account-target="register"]')?.click(), 0);
          }
          return;
        }

        const familyLink = e.target.closest('a[href]');
        if (familyLink && familyLink.closest('[data-family-account-links]')) {
          const path = familyLink.pathname;
          const text = familyLink.textContent.trim().toLowerCase();
          const isLogin    = path.startsWith('/account/login')    || text.includes('sign');
          const isRegister = path.startsWith('/account/register') || text.includes('join');
          if (isLogin || isRegister) {
            e.preventDefault();
            const accountId = document.querySelector('[data-modal-content^="account-drawer-"]')?.dataset.modalContent;
            if (accountId) {
              this.open(accountId);
              if (isRegister) setTimeout(() => document.querySelector('[data-account-target="register"]')?.click(), 0);
            }
            return;
          }
        }

        const trigger = e.target.closest('[data-modal-action]');
        if (!trigger) return;
        if (trigger.closest('[data-hover-trigger]') && document.activeElement !== trigger) return;

        const { modalAction: action, modalId: id } = trigger.dataset;
        if (action === 'open')     { e.preventDefault(); this.open(id); }
        else if (action === 'open-account') {
          e.preventDefault();
          const accountId = document.querySelector('[data-modal-content^="account-drawer-"]')?.dataset.modalContent;
          if (accountId) {
            this.open(accountId);
            if (trigger.dataset.accountSection) {
              setTimeout(() => document.querySelector(`[data-account-target="${trigger.dataset.accountSection}"]`)?.click(), 0);
            }
            if (trigger.dataset.returnTo) {
              const form = document.querySelector('form[action*="/account/login"]');
              if (form && !form.querySelector('[name="return_to"]')) {
                const input = document.createElement('input');
                input.type  = 'hidden';
                input.name  = 'return_to';
                input.value = trigger.dataset.returnTo;
                form.appendChild(input);
              }
            }
          }
        }
        else if (action === 'close')    this.close();
        else if (action === 'closeAll') this.closeAll();
      });
    }
  });
}
